import { useMemo } from "react";
import { DATA_STREAM_PERMISSIONS, hasDataPermission } from "@utils/data_access";
import type { DataStream } from "@utils/data_access";
import { useUserPermissions } from "./useUserPermissions";

export type DataAccess = {
  /**
   * True once the answer is actually known. Effects that fetch a gated stream must
   * wait on this — firing early sends a request that 403s, and skipping early means
   * never fetching at all. Team info carries the grants, so this normally flips as
   * soon as that one call lands.
   */
  ready: boolean;
  canAccess: (stream: DataStream) => boolean;
  canOverride: (stream: DataStream) => boolean;
  canDelete: (stream: DataStream) => boolean;
  /** True once permissions have loaded and the stream is denied. Safe to render on. */
  isDenied: (stream: DataStream) => boolean;
};

/**
 * Resolves every data-stream permission in one pass.
 *
 * Preferred over calling `usePermissionCheck("AccessGpsData")` five times: it reads
 * the store once and, unlike that hook, distinguishes "denied" from "not loaded yet".
 */
export const useDataAccess = (): DataAccess => {
  const userPermissions = useUserPermissions();

  return useMemo(() => {
    const ready = userPermissions != null;

    // Fails closed, matching `usePermissionCheck`: an absent key is a denied key.
    // Being optimistic here instead would mean that if `user_permissions` never
    // arrives, nothing ever locks — a permissions feature silently granting access.
    // Render-time flashing is handled by `isDenied`/`ready`, not by relaxing this.
    const check = (key: string | null) =>
      hasDataPermission(key, userPermissions);

    const canAccess = (stream: DataStream) =>
      check(DATA_STREAM_PERMISSIONS[stream]?.access ?? null);

    return {
      ready,
      canAccess,
      canOverride: (stream: DataStream) =>
        check(DATA_STREAM_PERMISSIONS[stream]?.override ?? null),
      canDelete: (stream: DataStream) =>
        check(DATA_STREAM_PERMISSIONS[stream]?.remove ?? null),
      isDenied: (stream: DataStream) => ready && !canAccess(stream),
    };
  }, [userPermissions]);
};
